/* ==========================================================================
   Alcoba Cómplice · ac-alcoba.js  — sin dependencias, < 4 KB minificado.
   Cargar con defer:  {{ 'ac-alcoba.js' | asset_url | script_tag }} no sirve (no lleva defer);
   usar <script src="{{ 'ac-alcoba.js' | asset_url }}" defer></script>
   Y en <head>, ANTES de los CSS, una línea en línea para evitar parpadeos:
   <script>document.documentElement.classList.add('js')</script>
   ========================================================================== */
(function () {
  'use strict';

  var root = document.documentElement;
  root.classList.add('js');
  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var hasIO = 'IntersectionObserver' in window;

  /* ---------- Aparición al hacer scroll: [data-reveal] ---------- */
  function initReveal(scope) {
    var items = (scope || document).querySelectorAll('[data-reveal]:not(.is-visible)');
    if (!items.length) return;
    if (reduceMotion || !hasIO) {
      items.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.12 });
    items.forEach(function (el) { io.observe(el); });
  }

  /* ---------- Navegación global: filete inferior al hacer scroll ---------- */
  function initNav() {
    var nav = document.querySelector('[data-nav]');
    if (!nav) return;
    var ticking = false;
    function update() {
      nav.classList.toggle('is-scrolled', window.scrollY > 4);
      ticking = false;
    }
    window.addEventListener('scroll', function () {
      if (!ticking) { window.requestAnimationFrame(update); ticking = true; }
    }, { passive: true });
    update();
  }

  /* ---------- Menú móvil: [data-menu-toggle] con aria-controls ---------- */
  function initMenu() {
    document.querySelectorAll('[data-menu-toggle]').forEach(function (btn) {
      if (btn.dataset.bound) return;
      btn.dataset.bound = '1';
      var menu = document.getElementById(btn.getAttribute('aria-controls'));
      if (!menu) return;
      var bar = btn.closest('[data-nav]');
      function setOpen(open) {
        /* El menú empieza justo bajo la barra, esté o no a la vista la franja superior */
        if (open && bar) menu.style.top = Math.max(0, Math.round(bar.getBoundingClientRect().bottom)) + 'px';
        menu.classList.toggle('is-open', open);
        btn.setAttribute('aria-expanded', String(open));
        btn.setAttribute('aria-label', open ? 'Cerrar menú' : 'Abrir menú');
        root.style.overflow = open ? 'hidden' : '';
        if (open) {
          var first = menu.querySelector('a, button');
          if (first) first.focus({ preventScroll: true });
        }
      }
      btn.addEventListener('click', function () {
        setOpen(btn.getAttribute('aria-expanded') !== 'true');
      });
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && menu.classList.contains('is-open')) { setOpen(false); btn.focus(); }
      });
    });
  }

  /* ---------- Acordeones que en escritorio van siempre abiertos: [data-open-desktop] details ---------- */
  function initOpenDesktop() {
    var groups = document.querySelectorAll('[data-open-desktop] details');
    if (!groups.length || !window.matchMedia) return;
    var mq = window.matchMedia('(min-width: 768px)');
    function apply() { groups.forEach(function (d) { d.open = mq.matches; }); }
    apply();
    if (mq.addEventListener) mq.addEventListener('change', apply); else mq.addListener(apply);
  }

  /* ---------- Estantes horizontales: [data-shelf] con [data-shelf-prev]/[data-shelf-next] ---------- */
  function initShelves(scope) {
    (scope || document).querySelectorAll('[data-shelf]').forEach(function (shelf) {
      if (shelf.dataset.bound) return;
      shelf.dataset.bound = '1';
      var track = shelf.querySelector('[data-shelf-track]');
      var prev = shelf.querySelector('[data-shelf-prev]');
      var next = shelf.querySelector('[data-shelf-next]');
      if (!track || !prev || !next) return;
      function step() {
        var item = track.querySelector('[data-shelf-item]');
        var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
        var per = item ? item.getBoundingClientRect().width + gap : track.clientWidth * 0.8;
        return Math.max(per, Math.floor(track.clientWidth / per) * per);
      }
      function update() {
        var max = track.scrollWidth - track.clientWidth - 2;
        prev.disabled = track.scrollLeft <= 2;
        next.disabled = track.scrollLeft >= max;
        var overflow = track.scrollWidth > track.clientWidth + 2;
        prev.hidden = next.hidden = !overflow;
      }
      prev.addEventListener('click', function () {
        track.scrollBy({ left: -step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      next.addEventListener('click', function () {
        track.scrollBy({ left: step(), behavior: reduceMotion ? 'auto' : 'smooth' });
      });
      track.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
      window.addEventListener('resize', update);
      update();
    });
  }

  /* ---------- Recomendaciones de Shopify: [data-recs-url] sustituye su respaldo si llegan tarjetas ---------- */
  function initRecs() {
    var host = document.querySelector('[data-recs-url]');
    if (!host || !window.fetch || !window.DOMParser) return;
    fetch(host.getAttribute('data-recs-url'))
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var fresh = new DOMParser().parseFromString(html, 'text/html').querySelector('.ac-recs');
        if (!fresh || !fresh.querySelector('.ac-card')) return;
        host.innerHTML = fresh.innerHTML;
        initShelves(host);
        initReveal(host);
      })
      .catch(function () { /* se queda el respaldo */ });
  }

  /* ---------- "Ver más productos": añade la página siguiente a [data-grid] sin recargar ----------
     Sin JavaScript, el enlace lleva a la página siguiente de toda la vida. */
  function initLoadMore() {
    if (!window.fetch || !window.DOMParser) return;
    document.addEventListener('click', function (e) {
      var link = e.target.closest('[data-load-more]');
      var grid = document.querySelector('[data-grid]');
      if (!link || !grid) return;
      e.preventDefault();
      if (link.getAttribute('aria-busy') === 'true') return;
      link.setAttribute('aria-busy', 'true');
      var label = link.textContent;
      link.textContent = 'Cargando…';
      fetch(link.href)
        .then(function (r) { if (!r.ok) throw new Error(r.status); return r.text(); })
        .then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var items = doc.querySelectorAll('[data-grid] > li');
          var firstNew = null;
          Array.prototype.forEach.call(items, function (li) {
            var node = document.adoptNode(li);
            grid.appendChild(node);
            if (!firstNew) firstNew = node;
          });
          var box = link.closest('.ac-loadmore');
          var next = doc.querySelector('.ac-loadmore');
          if (box && next) box.replaceWith(document.adoptNode(next)); else if (box) box.remove();
          /* Teclado y lector de pantalla: el foco pasa al primer producto nuevo */
          var target = firstNew && firstNew.querySelector('a[href]');
          if (target) target.focus({ preventScroll: true });
        })
        .catch(function () { link.textContent = label; window.location.href = link.href; });
    });
  }

  /* ---------- [data-autosubmit]: al cambiar el campo se envía su formulario (cantidades del carrito) ---------- */
  function initAutosubmit() {
    document.addEventListener('change', function (e) {
      var field = e.target.closest('[data-autosubmit]');
      if (!field || !field.form) return;
      if (field.form.requestSubmit) field.form.requestSubmit(); else field.form.submit();
    });
  }

  /* ---------- Barra de compra fija (móvil): [data-buybar] + [data-buy-anchor] ---------- */
  function initBuyBar() {
    var bar = document.querySelector('[data-buybar]');
    var anchor = document.querySelector('[data-buy-anchor]');
    if (!bar || !anchor || !hasIO) return;
    var io = new IntersectionObserver(function (entries) {
      var e = entries[0];
      var passed = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.classList.toggle('is-visible', passed);
      bar.setAttribute('aria-hidden', String(!passed));
      bar.querySelectorAll('a, button').forEach(function (el) { el.tabIndex = passed ? 0 : -1; });
    });
    io.observe(anchor);
  }

  /* ---------- Navegación local: marca el apartado visible ---------- */
  function initLocalNav() {
    var nav = document.querySelector('[data-localnav]');
    if (!nav || !hasIO) return;
    var links = Array.prototype.slice.call(nav.querySelectorAll('a[href^="#"]'));
    var map = {};
    links.forEach(function (a) {
      var target = document.getElementById(a.getAttribute('href').slice(1));
      if (target) map[target.id] = a;
    });
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (entry.isIntersecting && map[entry.target.id]) {
          links.forEach(function (a) { a.removeAttribute('aria-current'); });
          map[entry.target.id].setAttribute('aria-current', 'true');
        }
      });
    }, { rootMargin: '-40% 0px -55% 0px' });
    Object.keys(map).forEach(function (id) { io.observe(document.getElementById(id)); });
  }

  function init() {
    initReveal();
    initNav();
    initMenu();
    initOpenDesktop();
    initShelves();
    initBuyBar();
    initLocalNav();
    initRecs();
    initLoadMore();
    initAutosubmit();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  /* Editor de temas de Shopify: re-iniciar la sección que se recarga */
  document.addEventListener('shopify:section:load', function (e) {
    initReveal(e.target);
    initShelves(e.target);
    initMenu();
  });
})();
